/**
 * Drive local API token + first-party re-auth to CYNAYD One /apps/resume.
 */

const TOKEN_KEY = 'cynayd_drive_api_token';
const REAUTH_ATTEMPT_KEY = 'cynayd_drive_reauth_attempt';
const LEGACY_TOKEN_KEY = 'token';
const LEGACY_REFRESH_KEY = 'refreshToken';
const LEGACY_SSO_API_KEY = 'ssoApiToken';

/** Max age of a reauth attempt record before a new attempt is allowed. */
export const REAUTH_ATTEMPT_TTL_MS = 3 * 60 * 1000;

export const DRIVE_APP_SLUG =
  (typeof process !== 'undefined' &&
    process.env.REACT_APP_SSO_APP_SLUG?.trim()) ||
  'cynayd-drive';

export type ReauthReason = 'MISSING_TOKEN' | 'API_401' | 'BOOTSTRAP_401';

export type ReauthAttempt = {
  startedAt: number;
  returnUrl: string;
  reason: ReauthReason;
  attempt: number;
};

const AUTH_QUERY_PARAMS = ['sso_token', 'sso_code', 'code', 'state'] as const;
const UNSAFE_FRAGMENT =
  /(?:^|[&#?])(access_token|id_token|sso_token|sso_code|code|state|token)=/i;

function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function getOneOrigin(): string {
  const raw =
    (typeof process !== 'undefined' &&
      process.env.REACT_APP_ONE_ORIGIN?.trim()) ||
    'https://one.cynayd.com';
  return raw.replace(/\/$/, '');
}

function clearLegacyTokenKeys(store: Storage): void {
  try {
    store.removeItem(LEGACY_TOKEN_KEY);
    store.removeItem(LEGACY_REFRESH_KEY);
    store.removeItem(LEGACY_SSO_API_KEY);
  } catch {
    /* ignore */
  }
}

/** Read Drive API token; one-time migrate from legacy `token` / sessionStorage. */
export function getDriveApiToken(): string | null {
  const store = storage();
  if (!store) return null;
  try {
    const existing = store.getItem(TOKEN_KEY);
    if (existing) return existing;

    // Migrate legacy Drive SPA token key
    const legacyToken = store.getItem(LEGACY_TOKEN_KEY);
    if (legacyToken) {
      store.setItem(TOKEN_KEY, legacyToken);
      clearLegacyTokenKeys(store);
      return legacyToken;
    }

    // Migrate per-tab sessions from before localStorage switch
    const sessionLegacy = sessionStorage.getItem(TOKEN_KEY);
    if (sessionLegacy) {
      store.setItem(TOKEN_KEY, sessionLegacy);
      sessionStorage.removeItem(TOKEN_KEY);
      return sessionLegacy;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function setDriveApiToken(token: string): void {
  const store = storage();
  if (!store) return;
  store.setItem(TOKEN_KEY, token);
  clearLegacyTokenKeys(store);
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function clearDriveApiToken(): void {
  try {
    const store = storage();
    store?.removeItem(TOKEN_KEY);
    if (store) clearLegacyTokenKeys(store);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/** @deprecated Prefer getDriveApiToken */
export const getApiToken = getDriveApiToken;
/** @deprecated Prefer setDriveApiToken */
export const setApiToken = setDriveApiToken;
/** @deprecated Prefer clearDriveApiToken */
export const clearApiToken = clearDriveApiToken;

export function signOutDrive(): void {
  clearDriveApiToken();
  clearReauthAttempt();
  if (typeof window !== 'undefined') {
    window.location.replace('/');
  }
}

export const DRIVE_API_TOKEN_STORAGE_KEY = TOKEN_KEY;

export function isPublicDrivePath(pathname: string | null | undefined): boolean {
  return Boolean(pathname?.startsWith('/share/'));
}

/** Strip exchange credentials and unsafe fragment keys for returnUrl / address bar. */
export function sanitizeDriveReturnUrl(href: string): string {
  try {
    const u = new URL(href);
    for (const key of AUTH_QUERY_PARAMS) {
      u.searchParams.delete(key);
    }
    if (u.hash && UNSAFE_FRAGMENT.test(u.hash)) {
      u.hash = '';
    }
    return u.toString();
  } catch {
    return href;
  }
}

/** @deprecated Prefer sanitizeDriveReturnUrl */
export const sanitizeGridReturnUrl = sanitizeDriveReturnUrl;

export function getReauthAttempt(): ReauthAttempt | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(REAUTH_ATTEMPT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ReauthAttempt;
    if (
      !parsed ||
      typeof parsed.startedAt !== 'number' ||
      typeof parsed.returnUrl !== 'string'
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearReauthAttempt(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(REAUTH_ATTEMPT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Whether another One redirect is allowed. After a recent attempt for the same
 * returnUrl, block (caller shows terminal error). Stale attempts are cleared.
 */
export function canBeginReauth(returnUrl: string): boolean {
  const existing = getReauthAttempt();
  if (!existing) return true;
  if (Date.now() - existing.startedAt > REAUTH_ATTEMPT_TTL_MS) {
    clearReauthAttempt();
    return true;
  }
  if (existing.returnUrl !== returnUrl && existing.attempt < 2) {
    return true;
  }
  return false;
}

function writeReauthAttempt(attempt: ReauthAttempt): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(REAUTH_ATTEMPT_KEY, JSON.stringify(attempt));
  } catch {
    /* ignore */
  }
}

let reauthRedirecting = false;

/**
 * Clear dead local token and replace to One /apps/resume with deep returnUrl.
 * Single-flight: concurrent callers share the same redirect.
 */
export function beginDriveReauth(options: {
  reason: ReauthReason;
  returnUrl?: string;
}): boolean {
  if (typeof window === 'undefined') return false;
  if (isPublicDrivePath(window.location.pathname)) return false;
  if (reauthRedirecting) return true;

  const returnUrl = sanitizeDriveReturnUrl(
    options.returnUrl ?? window.location.href
  );

  if (!canBeginReauth(returnUrl)) {
    return false;
  }

  reauthRedirecting = true;
  clearDriveApiToken();

  const prev = getReauthAttempt();
  writeReauthAttempt({
    startedAt: Date.now(),
    returnUrl,
    reason: options.reason,
    attempt: (prev?.attempt ?? 0) + 1
  });

  const resume = new URL(`${getOneOrigin()}/apps/resume`);
  resume.searchParams.set('appSlug', DRIVE_APP_SLUG);
  resume.searchParams.set('returnUrl', returnUrl);
  window.location.replace(resume.toString());
  return true;
}

/** Mark successful exchange so a later session failure is a fresh attempt. */
export function markReauthExchangeSuccess(): void {
  clearReauthAttempt();
  reauthRedirecting = false;
}

export function buildResumeUrlForCurrentPage(): string {
  const returnUrl = sanitizeDriveReturnUrl(
    typeof window !== 'undefined' ? window.location.href : ''
  );
  const resume = new URL(`${getOneOrigin()}/apps/resume`);
  resume.searchParams.set('appSlug', DRIVE_APP_SLUG);
  resume.searchParams.set('returnUrl', returnUrl);
  return resume.toString();
}

/**
 * True when API body indicates auth session must be refreshed via One.
 * Does not treat 403 / access denied as session expiry.
 */
export function isAuthSessionExpiredResponse(
  status: number,
  body: unknown
): boolean {
  if (status !== 401) return false;
  if (!body || typeof body !== 'object') {
    return true;
  }
  const rec = body as Record<string, unknown>;
  const code =
    (typeof rec.code === 'string' && rec.code) ||
    (rec.error &&
      typeof rec.error === 'object' &&
      rec.error &&
      typeof (rec.error as { code?: unknown }).code === 'string' &&
      (rec.error as { code: string }).code) ||
    null;
  if (code === 'AUTH_SESSION_EXPIRED') return true;
  if (code === 'ACCESS_DENIED' || code === 'ACCESS_EPOCH_STALE') return false;
  const message =
    typeof rec.message === 'string'
      ? rec.message
      : Array.isArray(rec.message)
        ? rec.message.join(' ')
        : '';
  if (/jwt expired|invalid Bearer|Missing or invalid Bearer/i.test(message)) {
    return true;
  }
  if (
    typeof rec.message === 'object' &&
    rec.message &&
    (rec.message as { code?: string }).code === 'AUTH_SESSION_EXPIRED'
  ) {
    return true;
  }
  return code == null;
}
